import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useVoice } from '@humeai/voice-react';
import humeSimpleTTS from '../services/HumeSimpleTTS';

// Simplified HUME EVI Component - One-way TTS only
const HumeSimpleEVI = forwardRef(({ isActive, autoConnect = false }, ref) => {
  const {
    connect,
    disconnect,
    readyState,
    sendUserInput,
    sendAssistantInput,
    messages,
    mute,
    unmute,
    isMuted,
    isPlaying
  } = useVoice();

  const connectionStateRef = useRef(false);
  
  // Debug: Monitor messages from HUME
  useEffect(() => {
    if (messages && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      console.log('🎯 HUME message received:', {
        role: lastMessage.role,
        content: lastMessage.content?.substring(0, 100),
        timestamp: new Date().toISOString()
      });
    }
  }, [messages]);
  
  // Initialize the simple TTS service when connected
  // Note: HUME SDK has a bug where readyState stays 'idle' even when connected
  useEffect(() => {
    console.log('🔌 HUME readyState:', readyState);
    console.log('🔌 HUME functions available:', {
      connect: !!connect,
      sendUserInput: !!sendUserInput,
      sendAssistantInput: !!sendAssistantInput,
      mute: !!mute,
      messages: messages?.length || 0
    });
    
    // Only consider truly connected state
    const isConnected = readyState === 'connected';
    
    if (isConnected && !connectionStateRef.current && sendUserInput) {
      connectionStateRef.current = true;
      
      // Initialize the simple TTS service with voice hook
      humeSimpleTTS.init({
        sendAssistantInput: (text) => {
          try {
            console.log('🗣️ Attempting to speak via HUME:', text);
            console.log('🗣️ Current state:', { readyState, isPlaying, isMuted });
            
            // Method 1: Try sendUserInput with a prompt
            // This makes the assistant respond with encouragement
            const prompt = `Say exactly: "${text}"`;
            console.log('🗣️ Sending user prompt:', prompt);
            sendUserInput(prompt);
            
            // Method 2: Also try direct assistant input (may not work)
            // setTimeout(() => {
            //   sendAssistantInput(text);
            // }, 100);
            
            return true;
          } catch (error) {
            console.error('❌ Error sending to HUME:', error);
            return false;
          }
        }
      });
      
      // Test the connection with a welcome message
      setTimeout(() => {
        console.log('🔊 Testing HUME voice connection...');
        console.log('🔊 Current state:', { readyState, isMuted, hasFunction: !!sendUserInput });
        
        try {
          // Send a test message using user input to trigger assistant response
          const testPrompt = 'Say: "Voice encouragement is now active!"';
          console.log('🔊 Sending test prompt:', testPrompt);
          sendUserInput(testPrompt);
        } catch (err) {
          console.error('❌ Test message failed:', err);
        }
      }, 1500);
      
      // Mute microphone by default (one-way communication)
      if (mute) mute();
      
      console.log('✅ HUME Simple EVI connected (one-way TTS mode)');
      
      // Store connection state globally
      window.humeConnected = true;
      
    } else if (readyState === 'disconnected' && connectionStateRef.current) {
      connectionStateRef.current = false;
      humeSimpleTTS.disconnect();
      window.humeConnected = false;
      console.log('❌ HUME Simple EVI disconnected');
    }
  }, [readyState, sendUserInput, sendAssistantInput, mute]);

  // Auto-connect on mount if requested
  useEffect(() => {
    if (autoConnect && readyState === 'disconnected') {
      console.log('🔌 Auto-connecting HUME Simple EVI...');
      connect()
        .then(() => console.log('✅ HUME connected successfully'))
        .catch(err => console.error('❌ HUME connection failed:', err));
    }
  }, [autoConnect, readyState, connect]);

  // Handle active state changes
  useEffect(() => {
    if (isActive) {
      if (readyState === 'disconnected') {
        console.log('🔌 Connecting HUME Simple EVI...');
        connect()
          .then(() => {
            console.log('✅ HUME connect() promise resolved');
            // Wait a bit for the connection to stabilize
            setTimeout(() => {
              console.log('🔌 Checking HUME status after connect:', {
                readyState,
                hasFunction: !!sendAssistantInput
              });
            }, 1000);
          })
          .catch(err => console.error('❌ Connection failed:', err));
      } else {
        console.log('🔌 HUME already in state:', readyState);
      }
    } else if (!isActive && (readyState === 'connected' || readyState === 'idle')) {
      console.log('🔌 Disconnecting HUME Simple EVI...');
      disconnect();
    }
  }, [isActive]); // Remove readyState from deps to avoid loops

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    connect: async () => {
      if (readyState === 'disconnected') {
        try {
          await connect();
          return true;
        } catch (error) {
          console.error('Connection error:', error);
          return false;
        }
      }
      return readyState === 'connected';
    },
    disconnect: () => {
      if (readyState === 'connected') {
        disconnect();
      }
    },
    isConnected: () => readyState === 'connected',
    speak: (text) => {
      if (readyState === 'connected') {
        sendAssistantInput(text);
        return true;
      }
      return false;
    },
    getStatus: () => ({
      readyState,
      isMuted,
      ttsStatus: humeSimpleTTS.getStatus()
    })
  }), [readyState, connect, disconnect, sendAssistantInput, isMuted]);

  // No UI - this is a headless component
  return null;
});

HumeSimpleEVI.displayName = 'HumeSimpleEVI';

export default HumeSimpleEVI;